/**
 * Advanced Search and Filter Panel
 * Unified filtering system for all marketplace categories
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Divider,
  Rating,
  Grid,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import {
  ExpandMore,
  Search,
  FilterList,
  Clear,
  LocationOn,
  Star,
  LocalShipping,
  Verified,
  Schedule,
  Category
} from '@mui/icons-material';
import { formatCurrency } from '../../../utils/helpers';

const SearchFilterPanel = ({
  category,
  filters,
  onFiltersChange,
  onSearch,
  searchTerm,
  onSearchChange,
  totalResults = 0,
  isLoading = false
}) => {
  const [expandedPanels, setExpandedPanels] = useState(['search', 'price']);
  const [localFilters, setLocalFilters] = useState(filters);

  // Category-specific filter configurations
  const getFilterConfig = () => {
    const baseConfig = {
      price: { min: 0, max: 10000, step: 100 },
      location: ['Harare', 'Bulawayo', 'Gweru', 'Mutare', 'Masvingo', 'Chitungwiza'],
      rating: [1, 2, 3, 4, 5],
      features: ['Verified Vendor', 'Free Delivery', 'Same Day Service', 'Bulk Discount']
    };

    switch (category) {
      case 'materials':
        return {
          ...baseConfig,
          categories: ['Cement', 'Steel', 'Bricks', 'Roofing', 'Electrical', 'Plumbing', 'Paint', 'Tools'],
          units: ['Bags', 'Tonnes', 'Pieces', 'Meters', 'Liters', 'Sheets'],
          suppliers: ['Local', 'Imported', 'Manufacturer Direct'],
          availability: ['In Stock', 'Pre-Order', 'Made to Order']
        };

      case 'equipment':
        return {
          ...baseConfig,
          price: { min: 50, max: 2000, step: 50 },
          categories: ['Excavators', 'Cranes', 'Bulldozers', 'Mixers', 'Generators', 'Tools'],
          rentalPeriod: ['Daily', 'Weekly', 'Monthly', 'Long-term'],
          condition: ['New', 'Like New', 'Good', 'Fair'],
          features: [...baseConfig.features, 'Operator Included', '24/7 Support', 'Maintenance Included']
        };

      case 'professionals':
        return {
          ...baseConfig,
          price: { min: 20, max: 200, step: 10 },
          categories: ['Architects', 'Engineers', 'Project Managers', 'Consultants', 'Surveyors'],
          experience: ['0-2 years', '3-5 years', '6-10 years', '10+ years'],
          specializations: ['Residential', 'Commercial', 'Industrial', 'Infrastructure'],
          certifications: ['ZBCA', 'EIZ', 'RIBA', 'Local Authority'],
          availability: ['Available Now', 'Within 1 Week', 'Within 2 Weeks', 'By Appointment']
        };

      case 'insurance':
        return {
          ...baseConfig,
          price: { min: 100, max: 5000, step: 100 },
          categories: ['Construction Insurance', 'Completion Insurance', 'Equipment Insurance', 'Liability Insurance'],
          coverage: ['Basic', 'Comprehensive', 'Premium'],
          providers: ['Fidelity Life', 'Old Mutual', 'Zimnat', 'NicozDiamond'],
          duration: ['1 Year', '2 Years', '5 Years', 'Project Duration']
        };

      case 'inspectors':
        return {
          ...baseConfig,
          price: { min: 50, max: 500, step: 25 },
          categories: ['Foundation', 'Structural', 'Electrical', 'Plumbing', 'Final Inspection'],
          certifications: ['ZBCA Certified', 'City Council Approved', 'EIZ Registered'],
          availability: ['Same Day', 'Next Day', 'Within 3 Days', 'Within 1 Week'],
          reportTypes: ['Basic Report', 'Detailed Report', 'Photo Report', 'Video Report']
        };

      default:
        return baseConfig;
    }
  };

  const filterConfig = getFilterConfig();

  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => 
      isExpanded 
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    );
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...localFilters, [filterType]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePriceChange = (event, newValue) => {
    handleFilterChange('priceRange', newValue);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      categories: [],
      priceRange: [filterConfig.price.min, filterConfig.price.max],
      location: [],
      rating: 0,
      features: []
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onSearchChange('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (localFilters.categories?.length) count++;
    if (localFilters.location?.length) count++;
    if (localFilters.rating > 0) count++;
    if (localFilters.features?.length) count++;
    if (localFilters.priceRange?.[0] > filterConfig.price.min || 
        localFilters.priceRange?.[1] < filterConfig.price.max) count++;
    return count;
  };

  return (
    <Box sx={{ width: '300px', pr: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={getActiveFiltersCount()} color="primary">
            <FilterList />
          </Badge>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Tooltip title="Clear all filters">
          <IconButton onClick={clearAllFilters} size="small">
            <Clear />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {totalResults} {category} found
      </Typography>

      {/* Search */}
      <Accordion 
        expanded={expandedPanels.includes('search')} 
        onChange={handlePanelChange('search')}
        elevation={0}
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Search />
            <Typography>Search</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            placeholder={`Search ${category}...`}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            size="small"
          />
          <Button
            fullWidth
            variant="contained"
            onClick={() => onSearch(searchTerm)}
            sx={{ mt: 2 }}
            disabled={isLoading}
          >
            Search
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Categories */}
      {filterConfig.categories && (
        <Accordion 
          expanded={expandedPanels.includes('categories')} 
          onChange={handlePanelChange('categories')}
          elevation={0}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Category />
              <Typography>Categories</Typography>
              {localFilters.categories?.length > 0 && (
                <Chip size="small" label={localFilters.categories.length} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {filterConfig.categories.map((category) => (
                <FormControlLabel
                  key={category}
                  control={
                    <Checkbox
                      checked={localFilters.categories?.includes(category) || false}
                      onChange={(e) => {
                        const current = localFilters.categories || [];
                        const newCategories = e.target.checked
                          ? [...current, category]
                          : current.filter(c => c !== category);
                        handleFilterChange('categories', newCategories);
                      }}
                    />
                  }
                  label={category}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Price Range */}
      <Accordion 
        expanded={expandedPanels.includes('price')} 
        onChange={handlePanelChange('price')}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Price Range</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: 1 }}>
            <Slider
              value={localFilters.priceRange || [filterConfig.price.min, filterConfig.price.max]}
              onChange={handlePriceChange}
              min={filterConfig.price.min}
              max={filterConfig.price.max}
              step={filterConfig.price.step}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatCurrency(value)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2">
                {formatCurrency(localFilters.priceRange?.[0] || filterConfig.price.min)}
              </Typography>
              <Typography variant="body2">
                {formatCurrency(localFilters.priceRange?.[1] || filterConfig.price.max)}
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Location */}
      <Accordion 
        expanded={expandedPanels.includes('location')} 
        onChange={handlePanelChange('location')}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn />
            <Typography>Location</Typography>
            {localFilters.location?.length > 0 && (
              <Chip size="small" label={localFilters.location.length} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {filterConfig.location.map((location) => (
              <FormControlLabel
                key={location}
                control={
                  <Checkbox
                    checked={localFilters.location?.includes(location) || false}
                    onChange={(e) => {
                      const current = localFilters.location || [];
                      const newLocations = e.target.checked
                        ? [...current, location]
                        : current.filter(l => l !== location);
                      handleFilterChange('location', newLocations);
                    }}
                  />
                }
                label={location}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Rating */}
      <Accordion 
        expanded={expandedPanels.includes('rating')} 
        onChange={handlePanelChange('rating')}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star />
            <Typography>Minimum Rating</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <RadioGroup
            value={localFilters.rating || 0}
            onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
          >
            <FormControlLabel value={0} control={<Radio />} label="Any Rating" />
            {[4, 3, 2, 1].map((rating) => (
              <FormControlLabel
                key={rating}
                value={rating}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={rating} readOnly size="small" />
                    <Typography variant="body2">& up</Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </AccordionDetails>
      </Accordion>

      {/* Features */}
      <Accordion 
        expanded={expandedPanels.includes('features')} 
        onChange={handlePanelChange('features')}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified />
            <Typography>Features</Typography>
            {localFilters.features?.length > 0 && (
              <Chip size="small" label={localFilters.features.length} />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {filterConfig.features.map((feature) => (
              <FormControlLabel
                key={feature}
                control={
                  <Checkbox
                    checked={localFilters.features?.includes(feature) || false}
                    onChange={(e) => {
                      const current = localFilters.features || [];
                      const newFeatures = e.target.checked
                        ? [...current, feature]
                        : current.filter(f => f !== feature);
                      handleFilterChange('features', newFeatures);
                    }}
                  />
                }
                label={feature}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Category-specific filters */}
      {category === 'equipment' && filterConfig.rentalPeriod && (
        <Accordion 
          expanded={expandedPanels.includes('rental')} 
          onChange={handlePanelChange('rental')}
          elevation={0}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule />
              <Typography>Rental Period</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <RadioGroup
              value={localFilters.rentalPeriod || ''}
              onChange={(e) => handleFilterChange('rentalPeriod', e.target.value)}
            >
              {filterConfig.rentalPeriod.map((period) => (
                <FormControlLabel
                  key={period}
                  value={period}
                  control={<Radio />}
                  label={period}
                />
              ))}
            </RadioGroup>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Apply Filters Button */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => onFiltersChange(localFilters)}
          disabled={isLoading}
          sx={{ mb: 1 }}
        >
          Apply Filters
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={clearAllFilters}
          size="small"
        >
          Clear All
        </Button>
      </Box>
    </Box>
  );
};

export default SearchFilterPanel;